/**
 * Search Page
 * Date and guest selection form
 */

import { t, formatDate, getLanguage } from '../i18n';
import { Header, Card, renderServiceTabs, AppVersionFooter, FloatingNav, BackToTopButton, initBackToTop } from '../components';
import { openAvailabilityCalendar } from '../components/availability-calendar-modal';
import { renderCarousel } from '../components/carousel';
import { getState, setSearch, setAvailableRooms, navigateTo, setLoading, setError } from '../store';
import { searchRooms, getHotelInfo, fetchActiveServicesConfig, type HotelInfo } from '../api';
import { MAX_ADULTS, MAX_CHILDREN, MAX_ROOMS } from '../config';
import type { SearchCriteria } from '../types';
import Litepicker from 'litepicker';
import 'litepicker/dist/plugins/mobilefriendly';

let cachedHotelInfo: HotelInfo | null = null;

/**
 * Get tomorrow's date as default check-in
 */
function getDefaultCheckIn(): string {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().split('T')[0];
}

/**
 * Get day after tomorrow as default check-out
 */
function getDefaultCheckOut(): string {
  const date = new Date();
  date.setDate(date.getDate() + 2);
  return date.toISOString().split('T')[0];
}

/**
 * Render search page
 */
export function renderSearchPage(): string {
  const state = getState();
  const search = state.search || {
    checkIn: getDefaultCheckIn(),
    checkOut: getDefaultCheckOut(),
    adults: 2,
    children: 0,
    rooms: 1,
  };

  // Format dates for display
  const checkInFormatted = formatDate(search.checkIn);
  const checkOutFormatted = formatDate(search.checkOut);
  const hotelTitle = cachedHotelInfo?.hotelName || t('searchTitle');

  return `
    ${Header(hotelTitle)}
    ${renderServiceTabs('search')}
    
    <div class="page-content" id="search-content" style="padding: 16px; padding-bottom: 100px;">
      <!-- Hotel Name Pill Tab (above photo, matching Spa outlet style) -->
      <div id="hotel-pill-container" style="display:flex;gap:8px;overflow-x:auto;-webkit-overflow-scrolling:touch;touch-action:pan-x;padding-bottom:8px;margin-bottom:12px;scrollbar-width:none;-ms-overflow-style:none;">
        <button
          id="hotel-pill-tab"
          type="button"
          style="flex:0 0 auto;padding:8px 16px;border-radius:20px;border:1.5px solid #00B900;background:#00B900;color:#fff;font-size:13px;font-weight:600;white-space:nowrap;cursor:pointer;touch-action:manipulation;"
        >
          🏨 <span id="hotel-pill-name">${hotelTitle}</span>
        </button>
      </div>

      <!-- Hotel Showcase Carousel Card (Standardized 8px frame, 220px height) -->
      <div style="background:#fff;border-radius:14px;overflow:hidden;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);padding:8px;">
        <div class="room-images" id="hotel-images-container" style="position:relative;border-radius:10px;overflow:hidden;height:220px;background:#f5f5f5;margin:0;">
          <div style="width:100%;height:220px;background:linear-gradient(135deg, #00B900 0%, #008a00 100%);display:flex;align-items:center;justify-content:center;color:#fff;font-size:36px;">
            🏨
          </div>
        </div>
      </div>
      
      <form id="search-form" class="search-form">
        ${Card(`
          <div class="date-section-vertical">
            <div class="form-group date-range-group">
              <label class="form-label">📅 ${t('checkIn')} / ${t('checkOut')}</label>
              <div class="date-range-display" id="date-range-trigger">
                <div class="date-range-value">
                  <span class="date-label">${t('checkIn')}</span>
                  <span class="date-value" id="checkIn-display">${checkInFormatted}</span>
                </div>
                <span class="date-arrow">→</span>
                <div class="date-range-value">
                  <span class="date-label">${t('checkOut')}</span>
                  <span class="date-value" id="checkOut-display">${checkOutFormatted}</span>
                </div>
                <span class="date-toggle-icon">▼</span>
              </div>
              <!-- Hidden inputs for form submission -->
              <input type="hidden" id="checkIn" name="checkIn" value="${search.checkIn}" required />
              <input type="hidden" id="checkOut" name="checkOut" value="${search.checkOut}" required />
            </div>
            
            <!-- Calendar container for inline display (collapsed by default) -->
            <div id="litepicker-container" class="litepicker-inline collapsed"></div>
          </div>
        `)}
        
        ${Card(`
          <div class="guest-section">
            <div class="guest-row">
              <div class="guest-label">
                <span class="icon">👤</span>
                ${t('adults')}
              </div>
              <div class="counter">
                <button type="button" class="counter-btn" onclick="window.decrementCounter('adults')">−</button>
                <input type="number" id="adults" name="adults" value="${search.adults}" min="1" max="${MAX_ADULTS}" readonly />
                <button type="button" class="counter-btn" onclick="window.incrementCounter('adults')">+</button>
              </div>
            </div>
            
            <div class="guest-row">
              <div class="guest-label">
                <span class="icon">👶</span>
                ${t('children')}
              </div>
              <div class="counter">
                <button type="button" class="counter-btn" onclick="window.decrementCounter('children')">−</button>
                <input type="number" id="children" name="children" value="${search.children}" min="0" max="${MAX_CHILDREN}" readonly />
                <button type="button" class="counter-btn" onclick="window.incrementCounter('children')">+</button>
              </div>
            </div>
            
            <div class="guest-row">
              <div class="guest-label">
                <span class="icon">🛏️</span>
                ${t('rooms')}
              </div>
              <div class="counter">
                <button type="button" class="counter-btn" onclick="window.decrementCounter('rooms')">−</button>
                <input type="number" id="rooms" name="rooms" value="${search.rooms}" min="1" max="${MAX_ROOMS}" readonly />
                <button type="button" class="counter-btn" onclick="window.incrementCounter('rooms')">+</button>
              </div>
            </div>
          </div>
        `)}
        
        <div class="form-actions">
          <button type="submit" class="btn btn-primary btn-block btn-lg">
            🔍 ${t('searchButton')}
          </button>
          <div style="text-align: center; margin-top: 14px;">
            <button
              type="button"
              id="view-availability-cal-btn"
              style="
                background: none;
                border: none;
                padding: 6px 12px;
                color: #0088cc;
                font-size: 13px;
                font-weight: 700;
                text-decoration: underline;
                text-decoration-style: dotted;
                text-underline-offset: 4px;
                letter-spacing: 0.6px;
                cursor: pointer;
                text-transform: uppercase;
              "
            >
              ${t('viewAvailabilityCalendar')}
            </button>
          </div>
        </div>
      </form>
      
      ${AppVersionFooter()}
    </div>

    ${BackToTopButton('search')}

    ${FloatingNav()}
  `;
}

/**
 * Update max adults/children based on rooms count
 * Rule: max guests = BASE_MAX × rooms
 */
function updateGuestLimits(): void {
  const roomsInput = document.getElementById('rooms') as HTMLInputElement;
  const adultsInput = document.getElementById('adults') as HTMLInputElement;
  const childrenInput = document.getElementById('children') as HTMLInputElement;

  if (!roomsInput || !adultsInput || !childrenInput) return;

  const rooms = parseInt(roomsInput.value) || 1;

  // Base max per room (could come from hotel config later)
  const BASE_MAX_ADULTS_PER_ROOM = 4;
  const BASE_MAX_CHILDREN_PER_ROOM = 3;

  // Calculate new max based on rooms
  const newMaxAdults = Math.min(rooms * BASE_MAX_ADULTS_PER_ROOM, MAX_ADULTS);
  const newMaxChildren = Math.min(rooms * BASE_MAX_CHILDREN_PER_ROOM, MAX_CHILDREN);

  // Update max attributes
  adultsInput.max = newMaxAdults.toString();
  childrenInput.max = newMaxChildren.toString();

  // Clamp current values if they exceed new max
  const currentAdults = parseInt(adultsInput.value);
  const currentChildren = parseInt(childrenInput.value);

  if (currentAdults > newMaxAdults) {
    adultsInput.value = newMaxAdults.toString();
  }
  if (currentChildren > newMaxChildren) {
    childrenInput.value = newMaxChildren.toString();
  }

  // Ensure at least 1 adult per room
  const minAdults = rooms;
  adultsInput.min = minAdults.toString();
  if (currentAdults < minAdults) {
    adultsInput.value = minAdults.toString();
  }
}

/**
 * Initialize search page event handlers
 */
export async function initSearchPage(): Promise<void> {
  const form = document.getElementById('search-form');
  const checkInInput = document.getElementById('checkIn') as HTMLInputElement;
  const checkOutInput = document.getElementById('checkOut') as HTMLInputElement;
  const checkInDisplay = document.getElementById('checkIn-display');
  const checkOutDisplay = document.getElementById('checkOut-display');
  const pickerContainer = document.getElementById('litepicker-container');

  // Check if we're in edit mode (from URL param or sessionStorage)
  const urlParams = new URLSearchParams(window.location.search);
  const editBookingId = urlParams.get('editBooking') || sessionStorage.getItem('editBookingId');

  if (editBookingId) {
    console.log('[Search] Edit mode detected, booking ID:', editBookingId);
    await loadBookingForEdit(editBookingId);
  }

  // Load hotel info from API and update the card
  loadHotelInfo();

  // Initial guest limits update
  updateGuestLimits();

  // Initialize Litepicker for date range selection
  if (pickerContainer && checkInInput && checkOutInput) {
    // Toggle calendar visibility on date-range-display click
    const dateRangeTrigger = document.getElementById('date-range-trigger');
    if (dateRangeTrigger) {
      dateRangeTrigger.addEventListener('click', () => {
        pickerContainer.classList.toggle('collapsed');
        dateRangeTrigger.classList.toggle('expanded');
      });
    }

    const picker = new Litepicker({
      element: pickerContainer,
      inlineMode: true,
      singleMode: false,
      numberOfMonths: 1,
      numberOfColumns: 1,
      firstDay: 1,
      minDate: new Date(),
      minDays: 1,
      startDate: checkInInput.value,
      endDate: checkOutInput.value,
      format: 'YYYY-MM-DD',
      lang: ({ th: 'th-TH', en: 'en-US', zh: 'zh-CN', ru: 'ru-RU', ja: 'ja-JP', ko: 'ko-KR' } as Record<string, string>)[getLanguage()] ?? 'en-US',
      buttonText: {
        previousMonth: '‹',
        nextMonth: '›',
        apply: 'Apply',
        cancel: 'Cancel',
        reset: 'Reset',
      },
      tooltipText: {
        one: 'night',
        other: 'nights',
      },
      plugins: ['mobilefriendly'],
      mobileFriendly: true,
      setup: picker => {
        picker.on('selected', (start, end) => {
          if (start && end) {
            const startDate = start.format('YYYY-MM-DD');
            const endDate = end.format('YYYY-MM-DD');

            // Update hidden inputs
            checkInInput.value = startDate;
            checkOutInput.value = endDate;

            // Update display
            if (checkInDisplay) checkInDisplay.textContent = formatDate(startDate);
            if (checkOutDisplay) checkOutDisplay.textContent = formatDate(endDate);

            // Auto-hide calendar after selection complete
            setTimeout(() => {
              pickerContainer.classList.add('collapsed');
              const dateRangeTrigger = document.getElementById('date-range-trigger');
              if (dateRangeTrigger) dateRangeTrigger.classList.remove('expanded');
            }, 300);
          }
        });
      },
    });

    // Store picker instance for later access (e.g., edit mode)
    (window as any).__litepicker = picker;
  }

  // Counter functions
  window.incrementCounter = (name: string) => {
    const input = document.getElementById(name) as HTMLInputElement;
    const max = parseInt(input.max);
    const current = parseInt(input.value);
    if (current < max) {
      input.value = (current + 1).toString();
      // If rooms changed, update guest limits
      if (name === 'rooms') {
        updateGuestLimits();
      }
    }
  };

  window.decrementCounter = (name: string) => {
    const input = document.getElementById(name) as HTMLInputElement;
    const min = parseInt(input.min);
    const current = parseInt(input.value);
    if (current > min) {
      input.value = (current - 1).toString();
      // If rooms changed, update guest limits
      if (name === 'rooms') {
        updateGuestLimits();
      }
    }
  };

  // Form submission
  form?.addEventListener('submit', async e => {
    e.preventDefault();

    const formData = new FormData(form as HTMLFormElement);
    const search: SearchCriteria = {
      checkIn: formData.get('checkIn') as string,
      checkOut: formData.get('checkOut') as string,
      adults: parseInt(formData.get('adults') as string),
      children: parseInt(formData.get('children') as string),
      rooms: parseInt(formData.get('rooms') as string),
    };

    // Validate dates
    if (new Date(search.checkOut) <= new Date(search.checkIn)) {
      setError(t('selectDates'));
      return;
    }

    setSearch(search);
    setLoading(true);

    try {
      // Debug: Show API base URL
      const { getApiBaseUrl, getTenantId } = await import('../config');
      const apiBase = getApiBaseUrl();
      const tenantId = getTenantId();
      console.log('[Search] API Base:', apiBase, '| Tenant:', tenantId);
      console.log('[Search] Current hostname:', window.location.hostname);

      const rooms = await searchRooms(search);
      setAvailableRooms(rooms);
      navigateTo('rooms');
    } catch (error) {
      console.error('Search failed:', error);

      // Show error details on mobile
      const { getApiBaseUrl, getTenantId } = await import('../config');
      const apiBase = getApiBaseUrl();
      const tenantId = getTenantId();
      const errorMsg = error instanceof Error ? error.message : String(error);
      alert(
        `❌ เกิดข้อผิดพลาด\n\nAPI: ${apiBase}\nHost: ${window.location.hostname}\nTenant: ${tenantId}\n\nError: ${errorMsg}`
      );

      setError(t('errorNetwork'));
    } finally {
      setLoading(false);
    }
  });

  // Expose forward action to footer: submit the search form
  window.handleForward = () => {
    const formEl = document.getElementById('search-form') as HTMLFormElement | null;
    if (formEl) {
      if (typeof formEl.requestSubmit === 'function') {
        formEl.requestSubmit();
      } else {
        const btn = formEl.querySelector('button[type=submit]') as HTMLButtonElement | null;
        if (btn) btn.click();
      }
    }
  };

  // Availability Calendar trigger button
  const availCalBtn = document.getElementById('view-availability-cal-btn');
  availCalBtn?.addEventListener('click', () => {
    const checkInVal = (document.getElementById('checkIn') as HTMLInputElement)?.value;
    openAvailabilityCalendar(checkInVal);
  });

  // Floating "BACK TO TOP" for Search
  initBackToTop('search');
}

/**
 * Load hotel info from API and update Showcase Carousel
 */
async function loadHotelInfo(): Promise<void> {
  const imagesContainer = document.getElementById('hotel-images-container');
  const pillName = document.getElementById('hotel-pill-name');
  const headerTitle = document.querySelector('.header-title');

  try {
    const [hotelInfo] = await Promise.all([
      getHotelInfo(),
      fetchActiveServicesConfig(),
    ]);

    if (hotelInfo) {
      cachedHotelInfo = hotelInfo;

      // Update header title dynamically
      if (headerTitle && hotelInfo.hotelName) {
        headerTitle.textContent = hotelInfo.hotelName;
      }

      // Update hotel pill name above image
      if (pillName && hotelInfo.hotelName) {
        pillName.textContent = hotelInfo.hotelName;
      }

      // Render carousel
      if (imagesContainer) {
        const imageList = (hotelInfo.hotelImages && hotelInfo.hotelImages.length > 0)
          ? hotelInfo.hotelImages.map(url => ({ url }))
          : (hotelInfo.hotelImage ? [{ url: hotelInfo.hotelImage }] : []);

        if (imageList.length > 0) {
          renderCarousel(imagesContainer, imageList);
        }
      }
    }
  } catch (error) {
    console.error('[Search] Failed to load hotel info:', error);
  }
}

/**
 * Load booking data for editing
 */
async function loadBookingForEdit(bookingId: string): Promise<void> {
  try {
    let booking: any = null;
    const stored = sessionStorage.getItem('editBookingData');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.id === bookingId || parsed.confirmationNumber === bookingId) {
          booking = parsed;
        }
      } catch (_) {}
    }

    if (!booking) {
      const { getTenantId } = await import('../config');
      const { apiFetch } = await import('../api');
      const tenantId = getTenantId();

      const result = await apiFetch<{ success: boolean; data: any }>(
        `/api/v1/${tenantId}/bookings/${bookingId}`
      );

      if (!result.success || !result.data) {
        alert(t('errorLoadingBooking'));
        return;
      }
      booking = result.data;
    }

    console.log('[Search] Loaded booking for edit:', booking);

    // Store booking ID in sessionStorage for later use
    sessionStorage.setItem('editBookingId', bookingId);
    sessionStorage.setItem('editBookingData', JSON.stringify(booking));

    const checkIn = (booking.checkIn || booking.startAt || '').split('T')[0];
    const checkOut = (booking.checkOut || booking.endAt || '').split('T')[0];

    // Pre-fill form fields
    const checkInInput = document.getElementById('checkIn') as HTMLInputElement;
    const checkOutInput = document.getElementById('checkOut') as HTMLInputElement;
    const adultsInput = document.getElementById('adults') as HTMLInputElement;
    const childrenInput = document.getElementById('children') as HTMLInputElement;
    const roomsInput = document.getElementById('rooms') as HTMLInputElement;

    if (checkInInput && checkIn) checkInInput.value = checkIn;
    if (checkOutInput && checkOut) checkOutInput.value = checkOut;
    if (adultsInput && booking.adults) adultsInput.value = booking.adults.toString();
    if (childrenInput && booking.children !== undefined) childrenInput.value = booking.children.toString();
    if (roomsInput && booking.rooms) roomsInput.value = booking.rooms.toString();

    // Update Litepicker date range
    const picker = (window as any).__litepicker;
    if (picker && checkIn && checkOut) {
      picker.setDateRange(checkIn, checkOut);
    }

    // Update date display
    const checkInDisplay = document.getElementById('checkIn-display');
    const checkOutDisplay = document.getElementById('checkOut-display');
    if (checkInDisplay && checkIn) checkInDisplay.textContent = formatDate(checkIn);
    if (checkOutDisplay && checkOut) checkOutDisplay.textContent = formatDate(checkOut);

    // Update guest limits based on rooms
    updateGuestLimits();
  } catch (error) {
    console.error('[Search] Error loading booking for edit:', error);
    alert(t('errorNetwork'));
  }
}

// Extend window for global handlers
declare global {
  interface Window {
    incrementCounter: (name: string) => void;
    decrementCounter: (name: string) => void;
    handleForward?: () => void;
  }
}
